import { useCallback, useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Send, Bot, User, Sparkles, MessageSquare, Zap, ArrowLeft, Download, ScanSearch, Printer, FileText, X, Paperclip, RefreshCw, Smile } from "lucide-react";
import { API, apiError } from "../lib/api";
import { Spinner } from "../components/common";
import { tempColor, scoreColor, typeBadge, timeAgo } from "../lib/helpers";
import ChatMedia from "../components/ChatMedia";
import OpenWAChats from "./OpenWAChats";

const clock = value => {const d=new Date(value);return Number.isNaN(d.getTime())?"":d.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})};
const initials = name => String(name||"?").trim().split(/\s+/).slice(0,2).map(p=>p[0]).join("").toUpperCase();

export default function WhatsAppInbox() {
  const [searchParams] = useSearchParams();
  const [convos, setConvos] = useState(null);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [simText, setSimText] = useState("");
  const [sending, setSending] = useState(false);
  const [mobileThread, setMobileThread] = useState(false);
  const [whatsappLive, setWhatsappLive] = useState(false);
  const [quality, setQuality] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [sessionView, setSessionView] = useState(false);
  const [godsView, setGodsView] = useState(false);
  const [listError, setListError] = useState("");
  const [templateForm, setTemplateForm] = useState({ name:"", language:"en", parameters:[] });
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const followLatestRef = useRef(true);
  const activeLeadRef = useRef(null);
  const messageRequestRef = useRef(0);

  const openLead = useCallback((lead, showOnMobile = true) => {
    followLatestRef.current = true;
    activeLeadRef.current = lead.id;
    const request = ++messageRequestRef.current;
    setMessages([]);
    setActive(lead);
    setMobileThread(showOnMobile);
    API.get(`/leads/${lead.id}/messages`).then((r) => {if(activeLeadRef.current===lead.id&&messageRequestRef.current===request)setMessages(r.data)}).catch(e=>{if(activeLeadRef.current===lead.id)toast.error(apiError(e))});
  }, []);

  const loadConvos = useCallback(() => {
    const params = new URLSearchParams();
    if (type) params.set("lead_type", type);
    if (q) params.set("q", q);
    API.get(`/conversations?${params}`).then((r) => {
      setConvos(r.data);
      setListError("");
      const requested = searchParams.get("lead");
      const target = r.data.find((lead) => lead.id === requested) || r.data[0];
      if ((!active || (requested && active.id !== requested)) && target) openLead(target, Boolean(requested));
      if(!target){activeLeadRef.current=null;messageRequestRef.current++;setActive(null);setMessages([])}
    }).catch(e=>{setListError(apiError(e));setConvos([])});
  }, [type, q, active, openLead, searchParams]);
  useEffect(() => { const t = setTimeout(loadConvos, 200); return () => clearTimeout(t); }, [loadConvos]);
  useEffect(() => {
    API.get("/settings/integrations").then((r) => setWhatsappLive(Boolean(r.data.whatsapp_status?.live))).catch(() => {});
    API.get("/whatsapp-hub/templates").then(({data})=>setTemplates((Array.isArray(data)?data:[]).filter(t=>t.source==="openwa_live"&&["APPROVED","ACTIVE"].includes(String(t.status||"").toUpperCase())))).catch(()=>{});
  }, []);

  // Keep real WhatsApp traffic visible without re-selecting the conversation.
  useEffect(() => {
    if (!active?.id) return undefined;
    const refresh = async () => {
      const request = ++messageRequestRef.current;
      try {
        const [messageResponse, leadResponse] = await Promise.all([
          API.get(`/leads/${active.id}/messages`),
          API.get(`/leads/${active.id}`),
        ]);
        if(activeLeadRef.current!==active.id||messageRequestRef.current!==request)return;
        setMessages(messageResponse.data);
        setActive(leadResponse.data);
        setConvos((current) => current?.map((lead) => lead.id === leadResponse.data.id ? {...lead, ...leadResponse.data} : lead));
      } catch {}
    };
    const timer = setInterval(refresh, 3000);
    return () => clearInterval(timer);
  }, [active?.id]);

  useEffect(() => {
    if (followLatestRef.current) scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);

  const sendHuman = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await API.post(`/leads/${active.id}/messages`, { body: text, sender: "human" });
      setMessages((m) => [...m, data]); setText("");
      if (data.status === "blocked" && String(data.error||"").includes("saved template")) { setTemplateOpen(true); toast.info("The 24-hour window is closed. Choose a saved template instead."); }
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const sendAttachment = async (file) => {
    if (!file || !active) return;
    setSending(true);
    try {
      const form = new FormData(); form.append("file", file); form.append("caption", text);
      const {data} = await API.post(`/leads/${active.id}/media-message`, form);
      setMessages(m=>[...m,data]);setText("");
      toast.success(data.status==="sent"?"Media sent":"Media queued");
    } catch(e){toast.error(apiError(e))} finally {setSending(false);if(fileRef.current)fileRef.current.value=""}
  };

  const syncChat = async () => {
    if(!active)return;
    try { const {data}=await API.post(`/leads/${active.id}/messages/sync-openwa`);
      const response=await API.get(`/leads/${active.id}/messages`);setMessages(response.data);
      toast.success(`${data.imported} messages imported from OpenWA`);
    } catch(e){toast.error(apiError(e))}
  };

  const chooseTemplate = (name) => {
    const chosen = templates.find(t=>t.name===name);
    const indexes = [...new Set([...String(chosen?.content||"").matchAll(/\{\{(\d+)\}\}/g)].map(m=>Number(m[1])))];
    setTemplateForm({name,language:chosen?.language||"en",parameters:indexes.map((_,i)=>i===0?(active?.name||""):"")});
  };

  const sendTemplate = async () => {
    if (!active || !templateForm.name) return toast.error("Select a saved template");
    setSending(true);
    try {
      const {data}=await API.post(`/leads/${active.id}/template-message`,{template_name:templateForm.name,language:templateForm.language,parameters:templateForm.parameters});
      setMessages(m=>[...m,data]); setTemplateOpen(false); toast.success(data.status==="sent"?"Template message sent":"Template message queued");
    } catch(e){toast.error(apiError(e));} finally {setSending(false);}
  };

  const simulateIncoming = async () => {
    if (!simText.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await API.post(`/leads/${active.id}/simulate-incoming`, { body: simText });
      const add = [data.inbound];
      if (data.ai_reply) add.push(data.ai_reply);
      setMessages((m) => [...m, ...add]); setSimText("");
      API.get(`/leads/${active.id}`).then((r) => setActive(r.data));
      toast.success(data.flow_handled ? "Conversation flow handled the message" : data.ai_reply ? "AI replied automatically" : "Message received (AI paused)");
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const toggleAI = async () => {
    const { data } = await API.post(`/leads/${active.id}/toggle-ai`);
    setActive({ ...active, ai_mode: data.ai_mode });
    API.get(`/leads/${active.id}/messages`).then((r) => setMessages(r.data));
    toast.info(data.ai_mode ? "AI Agent resumed" : "Human takeover — AI paused");
  };

  const downloadConversation = async (format) => {
    if (!active) return;
    try {
      const response = await API.get(`/leads/${active.id}/conversation/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      if (format === "print") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const link = document.createElement("a"); link.href = url;
        link.download = `MB-NEXUS-${active.name || "lead"}-conversation.${format}`; link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };

  const analyzeConversation = async () => {
    try { const {data} = await API.post(`/leads/${active.id}/conversation/analyze`); setQuality(data); toast.success("Conversation quality audit complete"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if(sessionView || godsView)return <OpenWAChats godsOnly={godsView} onModeChange={setGodsView} onBack={()=>{setSessionView(false);setGodsView(false)}}/>;
  if (!convos) return <Spinner />;

  return (
    <div>
      <div className="flex items-center justify-between gap-3"><h1 className="font-heading text-2xl font-bold text-slate-100 mb-1">WhatsApp Inbox</h1><div className="flex flex-wrap gap-2"><button onClick={()=>setGodsView(true)} className="btn-secondary">Gods</button><button onClick={()=>setSessionView(true)} className="btn-secondary">All OpenWA chats</button></div></div>
      <p className="text-sm text-slate-400 mb-5">OpenWA linked session · <span className={whatsappLive ? "text-emerald-400" : "text-amber-400"}>{whatsappLive ? "Live WhatsApp" : "OpenWA offline"}</span></p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-slate-700/70 rounded-xl overflow-hidden h-[calc(100dvh-205px)] min-h-[520px] shadow-2xl">
        {/* list */}
        <div className={`${mobileThread ? "hidden lg:flex" : "flex"} lg:col-span-4 border-r border-slate-800 flex-col min-h-0 bg-[#0d1424] inbox-list`}>
          <div className="p-3 border-b border-slate-800 space-y-2"><div className="text-base font-semibold text-slate-100 px-1">Chats <span className="text-xs text-slate-500 font-normal">{convos.length}</span></div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats…" data-testid="inbox-search"
                className="w-full bg-slate-900/70 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40" />
            </div>
            <div className="inline-flex rounded-lg border border-slate-800 bg-slate-900/50 p-0.5 text-xs">
              {[["", "All"], ["INVESTOR", "Investors"], ["ASSOCIATE", "Associates"]].map(([v, l]) => (
                <button key={l} onClick={() => setType(v)} className={`px-3 py-1 rounded-md ${type === v ? "bg-amber-500/20 text-amber-300" : "text-slate-400"}`}>{l}</button>
              ))}
            </div>
            <div role="status" className="text-xs text-slate-400">{listError?`Could not load CRM chats: ${listError}`:q?convos.length?`${convos.length} CRM chat${convos.length===1?'':'s'} found`:`No CRM chat found for “${q}”. Try All OpenWA chats.`:`${convos.length} CRM chats`}</div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {!convos.length&&<div className="p-6 text-center text-sm text-slate-400">{q?"No name or number matched this CRM list.":"No CRM lead chats yet."}<button className="mt-3 block w-full text-emerald-300" onClick={()=>setSessionView(true)}>Search the paired OpenWA session →</button></div>}
            {convos.map((c) => (
              <div key={c.id} onClick={() => openLead(c, true)} data-testid={`convo-${c.id}`}
                className={`px-3 py-3 border-b border-slate-800/60 cursor-pointer transition ${active?.id === c.id ? "bg-slate-800/60" : "hover:bg-slate-800/30"}`}>
                <div className="flex gap-3"><div className="shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-teal-700 to-slate-700 grid place-items-center text-sm font-bold text-white">{initials(c.name)}</div><div className="min-w-0 flex-1"><div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-200 truncate">{c.name}</span>
                  <span className="text-[10px] text-slate-500 shrink-0">{timeAgo(c.last_message_at)}</span>
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message || "No messages"}</div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${typeBadge(c.lead_type)}`}>{c.lead_type}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${tempColor(c.temperature)}`}>{c.score}</span>
                  {c.ai_mode ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/25 flex items-center gap-0.5"><Bot className="h-2.5 w-2.5" />AI</span>
                    : <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-600/20 text-slate-400 border border-slate-600/40 flex items-center gap-0.5"><User className="h-2.5 w-2.5" />Human</span>}
                </div></div></div>
              </div>
            ))}
          </div>
        </div>

        {/* thread */}
        <div className={`${mobileThread ? "flex" : "hidden lg:flex"} lg:col-span-5 flex-col min-h-0 bg-[#0B1120] inbox-thread`}>
          {active ? (
            <>
              <div className="p-3 border-b border-slate-800 bg-[#182633] flex items-center justify-between gap-2">
                <button onClick={()=>setMobileThread(false)} className="lg:hidden mr-2 topbar-icon" aria-label="Back to conversations"><ArrowLeft className="h-4 w-4"/></button><div className="w-9 h-9 shrink-0 rounded-full bg-teal-700 grid place-items-center text-xs font-bold">{initials(active.name)}</div><div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-200">{active.name}</div>
                  <div className="text-xs text-slate-500">{active.phone}</div>
                </div>
                <div className="flex items-center gap-1"><button onClick={syncChat} className="topbar-icon" title="Import recent OpenWA chat history"><RefreshCw className="h-4 w-4"/></button><button onClick={()=>downloadConversation("json")} className="topbar-icon" title="Export chat with audit data"><Download className="h-4 w-4"/></button><button onClick={()=>downloadConversation("print")} className="topbar-icon" title="Print or save chat as PDF"><Printer className="h-4 w-4"/></button><button onClick={analyzeConversation} className="topbar-icon" title="Analyze conversation quality"><ScanSearch className="h-4 w-4"/></button></div>
                <button onClick={toggleAI} data-testid="whatsapp-ai-takeover-toggle"
                  className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border transition ${active.ai_mode ? "bg-purple-500/15 text-purple-300 border-purple-500/30" : "bg-slate-800 text-slate-300 border-slate-700"}`}>
                  {active.ai_mode ? <><Bot className="h-3.5 w-3.5" /> AI Mode</> : <><User className="h-3.5 w-3.5" /> Human Mode</>}
                </button>
              </div>
              <div ref={scrollRef} onScroll={(event) => {
                const el = event.currentTarget;
                followLatestRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
              }} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2 bg-[#0e1b21]" style={{backgroundImage:"radial-gradient(#ffffff09 1px, transparent 1px)",backgroundSize:"28px 28px"}}>
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm shadow-md whitespace-pre-wrap break-words ${m.direction === "out" ? "bg-[#075e54] text-white rounded-tr-sm" : "bg-[#263943] text-slate-100 rounded-tl-sm"}`}>
                      {String(m.sender).startsWith("ai") && <div className="text-[9px] uppercase tracking-wide opacity-70 mb-0.5 flex items-center gap-1"><Bot className="h-2.5 w-2.5" />MB AI Agent</div>}
                      {m.media_type&&<ChatMedia leadId={active.id} message={m}/>}
                      {!m.media_type||!/^\[[^\]]+ message\]$/.test(m.body||"")?m.body:null}
                      <div className="text-[10px] opacity-65 mt-1 text-right">{clock(m.created_at)} {m.direction==="out"?(m.status==="read"?"✓✓":m.status==="delivered"?"✓✓":"✓"):""}</div>
                      {m.error && <div className="text-[10px] mt-1 text-red-300">{m.error}</div>}
                    </div>
                  </div>
                ))}
                {messages.length === 0 && <div className="text-center text-slate-500 text-sm py-10">No messages yet.</div>}
              </div>
              <div className="shrink-0 sticky bottom-0 z-10 p-3 border-t border-slate-800 space-y-2 bg-[#0B1120]">
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" className="hidden" accept="image/jpeg,image/png,image/webp,video/mp4,application/pdf" onChange={e=>sendAttachment(e.target.files?.[0])}/>
                  <button type="button" onClick={()=>fileRef.current?.click()} disabled={sending} className="topbar-icon" title="Attach photo, video, or PDF"><Paperclip className="h-4 w-4"/></button>
                  <button type="button" onClick={()=>setEmojiOpen(!emojiOpen)} className="topbar-icon" title="Add emoji"><Smile className="h-4 w-4"/></button>
                  <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendHuman()}
                    placeholder="Type a reply (as human)…" data-testid="whatsapp-input"
                    className="flex-1 bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40" />
                  <button onClick={sendHuman} disabled={sending} className="btn-gold px-3.5" data-testid="whatsapp-send-btn"><Send className="h-4 w-4" /></button>
                  <button onClick={()=>setTemplateOpen(true)} disabled={sending||!templates.length} className="btn-secondary px-3" title="Send a saved template outside the 24-hour window"><FileText className="h-4 w-4"/></button>
                </div>
                {emojiOpen&&<div className="flex flex-wrap gap-1 rounded-lg border border-slate-700 p-2">{["😀","😊","👋","🙏","👍","❤️","🏡","📍","✨","📞","✅","📈","🎉","🔥","🙂","🤝"].map(emoji=><button type="button" key={emoji} onClick={()=>{setText(t=>t+emoji);setEmojiOpen(false)}} className="rounded px-1.5 py-1 text-lg hover:bg-slate-700">{emoji}</button>)}</div>}
                <div className="flex gap-2">
                  <input value={simText} onChange={(e) => setSimText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && simulateIncoming()}
                    placeholder="Simulate incoming lead message → triggers AI…" data-testid="simulate-input"
                    className="flex-1 bg-slate-900/40 border border-dashed border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-purple-500/40" />
                  <button onClick={simulateIncoming} disabled={sending} className="px-3.5 rounded-lg bg-purple-600/80 hover:bg-purple-500 text-white" data-testid="simulate-btn"><Zap className="h-4 w-4" /></button>
                </div>
              </div>
            </>
          ) : <div className="flex-1 flex items-center justify-center text-slate-500"><MessageSquare className="h-5 w-5 mr-2" /> Select a conversation</div>}
        </div>

        {/* AI drawer */}
        <div className="lg:col-span-3 border-l border-slate-800 bg-[#0d1424] p-4 overflow-y-auto hidden lg:block">
          {active ? (
            <>
              <div className="caption-mono mb-3">AI Intelligence</div>
              <div className="space-y-4">
                <div><div className="text-[11px] text-slate-500">Lead Score</div><div className={`font-heading text-3xl font-bold ${scoreColor(active.score)}`}>{active.score}</div></div>
                <div><div className="text-[11px] text-slate-500 mb-1">Temperature</div><span className={`text-xs px-2 py-1 rounded-full border capitalize ${tempColor(active.temperature)}`}>{active.temperature}</span></div>
                <div><div className="text-[11px] text-slate-500">Intent / Confidence</div><div className="text-sm text-slate-200 capitalize">{active.intent} · {active.confidence}%</div></div>
                <div className="pt-3 border-t border-slate-800">
                  <div className="text-[11px] text-slate-500 mb-1">AI Summary</div>
                  <div className="text-xs text-slate-300">{active.ai_summary || "—"}</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1"><Sparkles className="h-3 w-3 text-amber-400" />Recommended</div>
                  <div className="text-xs text-amber-300">{active.ai_recommended_action || "—"}</div>
                </div>
                {active.needs_human && <div className="text-xs bg-red-500/10 border border-red-500/25 text-red-300 rounded-lg p-2.5">⚠ Human escalation recommended</div>}
                {quality && <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/5 p-3 space-y-2"><div className="flex items-end justify-between"><div><div className="text-[10px] uppercase tracking-widest text-cyan-300">Conversation quality</div><div className="text-2xl font-bold text-white">{quality.score}<span className="text-xs text-slate-500">/100</span></div></div><span className="text-xs text-cyan-200">{quality.grade}</span></div>{quality.issues?.slice(0,4).map(issue=><div key={issue.code} className="text-[11px] text-slate-300">• {issue.label} ({issue.count})</div>)}{quality.recommendations?.[0]&&<div className="text-[11px] text-amber-300 border-t border-slate-800 pt-2">{quality.recommendations[0]}</div>}</div>}
              </div>
            </>
          ) : null}
        </div>
      </div>
      {templateOpen&&<div className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-4" onMouseDown={e=>e.target===e.currentTarget&&setTemplateOpen(false)}><div className="mb-card w-full max-w-lg p-5"><div className="flex items-start justify-between gap-3"><div><div className="section-kicker">ONE-TO-ONE TEMPLATE</div><h2 className="font-heading text-lg font-bold">Message {active?.name}</h2><p className="text-xs text-slate-500 mt-1">Send a saved OpenWA template to this contact. No campaign is required.</p></div><button onClick={()=>setTemplateOpen(false)} className="topbar-icon"><X className="h-4 w-4"/></button></div><label className="block text-xs mt-4">Saved template<select className="field mt-1" value={templateForm.name} onChange={e=>chooseTemplate(e.target.value)}><option value="">Select template…</option>{templates.map(t=><option key={`${t.meta_template_id||t.name}:${t.language}`} value={t.name}>{t.name} · {t.category} · {t.language}</option>)}</select></label>{templateForm.parameters.map((value,index)=><label className="block text-xs mt-3" key={index}>Value for {`{{${index+1}}}`}<input className="field mt-1" value={value} onChange={e=>setTemplateForm(f=>({...f,parameters:f.parameters.map((v,i)=>i===index?e.target.value:v)}))}/></label>)}{templateForm.name&&<div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3 mt-4 text-xs whitespace-pre-wrap">{(()=>{let preview=templates.find(t=>t.name===templateForm.name)?.content||"";templateForm.parameters.forEach((v,i)=>preview=preview.replaceAll(`{{${i+1}}}`,v||`{{${i+1}}}`));return preview})()}</div>}<button onClick={sendTemplate} disabled={sending||!templateForm.name||templateForm.parameters.some(v=>!v.trim())} className="btn-gold w-full mt-4 py-2.5"><Send className="h-4 w-4"/>{sending?"Sending…":"Send saved template"}</button></div></div>}
    </div>
  );
}
